import { useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { PageHeader } from '../../../components/ui/PageHeader'
import { Spinner } from '../../../components/ui/Spinner'
import { Alert } from '../../../components/ui/Alert'
import { selectClass } from '../../../components/ui/FormField'
import { api } from '../../../api/axios'
import { useAuth } from '../../../contexts/AuthContext'
import { useCampeonatos } from '../../campeonatos/hooks/useCampeonatos'
import { useModalidades } from '../../modalidades/hooks/useModalidades'
import { useInscricoes } from '../../inscricoes/hooks/useInscricoes'
import { useAtiradores } from '../../atiradores/hooks/useAtiradores'
import {
  useRelatorioResultadoIndividual,
  useRelatorioRankingGeral,
  useRelatorioMedalhas,
  useRelatorioParticipantes,
} from '../hooks/useRelatorios'
import type { RelatorioResult } from '../types'

// ── Tipos de abas ─────────────────────────────────────────────────────────
type TabType = 'individual' | 'ranking' | 'medalhas' | 'participantes' | 'resultados-etapa' | 'medalhas-etapa' | 'ranking-maior' | 'inscricoes-atirador' | 'inscricoes-geral' | 'habitualidade'

// ── Modal de visualizacao do PDF ──────────────────────────────────────────
function PdfPreviewModal({
  result,
  titulo,
  onClose,
}: {
  result: RelatorioResult
  titulo: string
  onClose: () => void
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  useEffect(() => {
    let active = true
    let createdUrl: string | null = null
    setLoading(true)
    setErro(null)
    api
      .get(`/relatorios/arquivo/${result.arquivo}`, { responseType: 'blob' })
      .then(resp => {
        if (!active) return
        const blob = new Blob([resp.data], { type: 'application/pdf' })
        createdUrl = URL.createObjectURL(blob)
        setBlobUrl(createdUrl)
      })
      .catch(err => {
        if (!active) return
        setErro(err?.response?.data?.error || err?.message || 'Erro ao carregar PDF')
      })
      .finally(() => { if (active) setLoading(false) })
    return () => {
      active = false
      if (createdUrl) URL.revokeObjectURL(createdUrl)
    }
  }, [result.arquivo])

  function handlePrint() {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.focus()
      iframeRef.current.contentWindow.print()
    }
  }

  function handleDownload() {
    if (!blobUrl) return
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = result.arquivo
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  function handleWhatsApp() {
    window.open(result.whatsapp, '_blank')
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex flex-col" onClick={onClose}>
      <div
        className="flex-1 flex flex-col bg-compumaq-pretoSuave m-4 rounded-xl overflow-hidden shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Cabecalho */}
        <div className="bg-compumaq-pretoSuave border-b border-compumaq-dourado/25 px-4 py-3 flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="font-semibold text-compumaq-texto">{titulo}</h3>
            <p className="text-xs text-compumaq-textoSuave">Pre-visualizacao</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handlePrint}
              disabled={!blobUrl}
              className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto rounded-lg text-sm font-medium hover:bg-compumaq-doradoClaro disabled:opacity-50"
            >
              Imprimir
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={!blobUrl}
              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
            >
              Baixar PDF
            </button>
            <button
              type="button"
              onClick={handleWhatsApp}
              disabled={!blobUrl}
              className="px-4 py-2 bg-green-700 text-white rounded-lg text-sm font-medium hover:bg-green-800 disabled:opacity-50"
            >
              WhatsApp
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-compumaq-pretoSuave text-compumaq-texto rounded-lg text-sm font-medium hover:bg-compumaq-pretoSuave"
            >
              Fechar
            </button>
          </div>
        </div>

        {/* Conteudo: iframe com PDF */}
        <div className="flex-1 bg-compumaq-pretoSuave relative">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-compumaq-pretoSuave rounded-xl px-6 py-4 flex items-center gap-3 shadow">
                <Spinner size="lg" />
                <span className="text-compumaq-texto">Carregando PDF...</span>
              </div>
            </div>
          )}
          {erro && (
            <div className="absolute inset-0 flex items-center justify-center p-4">
              <div className="bg-red-50 border border-red-200 rounded-xl p-6 max-w-md">
                <p className="font-semibold text-red-800 mb-2">Erro ao carregar PDF</p>
                <p className="text-sm text-red-700">{erro}</p>
              </div>
            </div>
          )}
          {blobUrl && !erro && (
            <iframe
              ref={iframeRef}
              src={blobUrl}
              className="w-full h-full border-0"
              title={titulo}
            />
          )}
        </div>
      </div>
    </div>
  )
}

// ── Componente principal ──────────────────────────────────────────────────
export function RelatoriosPage() {
  const { hasRole } = useAuth()
  const isAdmin = hasRole('admin')

  const [activeTab, setActiveTab] = useState<TabType>('individual')
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  // Filtros compartilhados
  const [selCampeonato, setSelCampeonato] = useState<number | ''>('')
  const [selModalidade, setSelModalidade] = useState<number | 'all' | ''>('')

  // Filtros específicos de cada aba
  const [selInscricao, setSelInscricao] = useState<number | ''>('')
  const [selTop, setSelTop] = useState<number>(3)

  // Filtro especifico para Resultados/Medalhas DA ETAPA
  const [selEtapa, setSelEtapa] = useState<number | 'todas' | ''>('')

  // Filtro de etapa para a aba "Entrega de Medalhas" (Top N) — vazio = todas as etapas (soma do campeonato)
  const [selEtapaMedalhas, setSelEtapaMedalhas] = useState<number | ''>('')

  // Resultados PDF gerados
  const [pdfIndividual, setPdfIndividual] = useState<RelatorioResult | null>(null)
  const [pdfRanking, setPdfRanking] = useState<RelatorioResult | null>(null)
  const [pdfMedalhas, setPdfMedalhas] = useState<RelatorioResult | null>(null)
  const [pdfParticipantes, setPdfParticipantes] = useState<RelatorioResult | null>(null)
  const [pdfResultadosEtapa, setPdfResultadosEtapa] = useState<RelatorioResult | null>(null)
  const [pdfMedalhasEtapa, setPdfMedalhasEtapa] = useState<RelatorioResult | null>(null)
  const [pdfRankingMaior, setPdfRankingMaior] = useState<RelatorioResult | null>(null)
  const [pdfInscricoesAt, setPdfInscricoesAt] = useState<RelatorioResult | null>(null)
  const [pdfInscricoesGeral, setPdfInscricoesGeral] = useState<RelatorioResult | null>(null)
  const [gerandoEtapa, setGerandoEtapa] = useState(false)
  const [gerandoRankMaior, setGerandoRankMaior] = useState(false)
  const [gerandoInscAt, setGerandoInscAt] = useState(false)
  const [gerandoInscGeral, setGerandoInscGeral] = useState(false)
  const [gerandoHabit, setGerandoHabit] = useState<'csv' | 'pdf' | null>(null)

  // Selecao do atirador (so admin) para o relatorio "Inscricoes do Atirador"
  const [selAtiradorRel, setSelAtiradorRel] = useState<number | ''>('')
  const [searchAtiradorRel, setSearchAtiradorRel] = useState('')
  const [selEtapaRel, setSelEtapaRel] = useState<number | ''>('')
  const [selModalidadeRel, setSelModalidadeRel] = useState<number | ''>('')
  const { data: atiradoresRelData } = useAtiradores(
    isAdmin && searchAtiradorRel.length >= 3 ? { nome: searchAtiradorRel, limit: 20 } : {},
  )
  const atiradoresRel = atiradoresRelData?.data ?? []
  // Todas as modalidades do sistema (sem filtrar por campeonato) — o
  // relatorio de inscricoes do atirador nao tem selecao de campeonato.
  const { data: modalidadesRelData } = useModalidades(isAdmin ? { limit: 200 } : {})
  const modalidadesRel = modalidadesRelData?.data ?? []

  // Filtros do relatorio "Todas as Inscricoes" (somente admin) — os 4 sao
  // independentes entre si, cada um com opcao "Todos/Todas".
  const [selCampeonatoInscGeral, setSelCampeonatoInscGeral] = useState<number | ''>('')
  const [selModalidadeInscGeral, setSelModalidadeInscGeral] = useState<number | ''>('')
  const [selEtapaInscGeral, setSelEtapaInscGeral] = useState<number | ''>('')
  const [selAtiradorInscGeral, setSelAtiradorInscGeral] = useState<number | ''>('')
  const [searchAtiradorInscGeral, setSearchAtiradorInscGeral] = useState('')
  const { data: atiradoresInscGeralData } = useAtiradores(
    isAdmin && searchAtiradorInscGeral.length >= 3 ? { nome: searchAtiradorInscGeral, limit: 20 } : {},
  )
  const atiradoresInscGeral = atiradoresInscGeralData?.data ?? []

  // Dados para selects
  const { data: campeonatosData } = useCampeonatos({ limit: 200 })
  const { data: modalidadesData } = useModalidades(
    selCampeonato ? { idcampeonato: selCampeonato as number, limit: 100 } : {},
  )
  const modalidades = selCampeonato ? (modalidadesData?.data ?? []) : []

  // Inscrições para relatório individual (filtradas por campeonato+modalidade)
  const { data: inscricoesData } = useInscricoes(
    selCampeonato && selModalidade && selModalidade !== 'all'
      ? { idcampeonato: selCampeonato as number, idmodalidade: selModalidade as number, status: 'ativa', limit: 500 }
      : {},
  )
  const inscricoes = selCampeonato && selModalidade && selModalidade !== 'all' ? (inscricoesData?.data ?? []) : []

  // Etapas para abas Resultados/Medalhas DA ETAPA (publico/etapas filtra por campeonato)
  const { data: etapasEtapaData } = useQuery({
    queryKey: ['etapas-publicas-rel', selCampeonato],
    queryFn: async () => {
      if (!selCampeonato) return [] as Array<{ idetapa: number; titulo: string; titulo_reduzido: string | null; numero_etapa: number }>
      const r = await api.get<{ data: Array<{ idetapa: number; titulo: string; titulo_reduzido: string | null; numero_etapa: number }> }>(
        '/publico/etapas', { params: { idcampeonato: selCampeonato } },
      )
      return r.data.data
    },
    enabled: !!selCampeonato,
  })
  const etapasEtapa = etapasEtapaData ?? []
  // `/publico/etapas` traz uma linha por modalidade para a mesma rodada (cada
  // modalidade tem sua propria linha na tabela etapa) - agrupamos por
  // numero_etapa para o filtro nao mostrar a mesma etapa repetida.
  const etapasEtapaUnicas = (() => {
    const vistas = new Map<number, typeof etapasEtapa[0]>()
    for (const e of etapasEtapa) {
      if (!vistas.has(e.numero_etapa)) vistas.set(e.numero_etapa, e)
    }
    return Array.from(vistas.values()).sort((a, b) => a.numero_etapa - b.numero_etapa)
  })()

  // Mutations
  const gerarIndividual = useRelatorioResultadoIndividual()
  const gerarRanking = useRelatorioRankingGeral()
  const gerarMedalhas = useRelatorioMedalhas()
  const gerarParticipantes = useRelatorioParticipantes()

  function resetPdfs() {
    setPdfIndividual(null)
    setPdfRanking(null)
    setPdfMedalhas(null)
    setPdfParticipantes(null)
    setPdfResultadosEtapa(null)
    setPdfMedalhasEtapa(null)
    setPdfRankingMaior(null)
    setPdfInscricoesAt(null)
    setPdfInscricoesGeral(null)
  }

  async function handleGerarInscricoesAtirador() {
    if (selAtiradorRel === '') return
    setAlert(null); setPdfInscricoesAt(null); setGerandoInscAt(true)
    try {
      const { data } = await api.get<{ data: RelatorioResult }>('/relatorios/inscricoes-atirador',
        { params: { idatirador: selAtiradorRel, numero_etapa: selEtapaRel || undefined, idmodalidade: selModalidadeRel || undefined } })
      setPdfInscricoesAt(data.data)
    } catch (err: any) {
      setAlert({ type: 'error', message: err?.response?.data?.error || 'Erro ao gerar relatorio de inscricoes do atirador' })
    } finally { setGerandoInscAt(false) }
  }

  async function handleGerarInscricoesGeral() {
    setAlert(null); setPdfInscricoesGeral(null); setGerandoInscGeral(true)
    try {
      const { data } = await api.get<{ data: RelatorioResult }>('/relatorios/inscricoes-geral', {
        params: {
          idcampeonato: selCampeonatoInscGeral || undefined,
          idmodalidade: selModalidadeInscGeral || undefined,
          numero_etapa: selEtapaInscGeral || undefined,
          idatirador: selAtiradorInscGeral || undefined,
        },
      })
      setPdfInscricoesGeral(data.data)
    } catch (err: any) {
      setAlert({ type: 'error', message: err?.response?.data?.error || 'Erro ao gerar relatorio de inscricoes' })
    } finally { setGerandoInscGeral(false) }
  }

  async function handleGerarRankingMaior() {
    if (selCampeonato === '') return
    setAlert(null); setPdfRankingMaior(null); setGerandoRankMaior(true)
    try {
      const params: any = { idcampeonato: selCampeonato }
      if (selModalidade !== '' && selModalidade !== 'all') params.idmodalidade = selModalidade
      const { data } = await api.get<{ data: RelatorioResult }>('/relatorios/ranking-maior-pontuacao', { params })
      setPdfRankingMaior(data.data)
    } catch (err: any) {
      setAlert({ type: 'error', message: err?.response?.data?.error || 'Erro ao gerar ranking maior pontuacao' })
    } finally { setGerandoRankMaior(false) }
  }

  // Habitualidade / DPA-BASE: CSV e PDF vem direto da API (tem CPF/CR, nao fica no link publico de arquivos)
  async function handleHabitualidade(formato: 'csv' | 'pdf') {
    if (selEtapa === '' || selEtapa === 'todas') return
    setAlert(null); setGerandoHabit(formato)
    try {
      const resp = await api.get<Blob>('/relatorios/habitualidade-etapa', { params: { idetapa: selEtapa, formato }, responseType: 'blob' })
      const disp = String(resp.headers['content-disposition'] ?? '')
      const nome = disp.match(/filename="([^"]+)"/)?.[1] ?? `habitualidade.${formato}`
      const url = URL.createObjectURL(resp.data)
      const a = document.createElement('a')
      a.href = url
      a.download = nome
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(url), 60000)
      setAlert({ type: 'success', message: `${formato === 'csv' ? 'Planilha' : 'PDF'} baixado: ${nome}` })
    } catch (err: any) {
      let msg = 'Erro ao gerar o relatorio de habitualidade'
      try { msg = JSON.parse(await (err?.response?.data as Blob).text()).error || msg } catch { /* resposta sem JSON */ }
      setAlert({ type: 'error', message: msg })
    } finally { setGerandoHabit(null) }
  }

  async function handleGerarResultadosEtapa() {
    if (selEtapa === '' || selCampeonato === '') return
    setAlert(null); setPdfResultadosEtapa(null); setGerandoEtapa(true)
    try {
      const params: any = { idcampeonato: selCampeonato }
      if (selEtapa !== 'todas') params.idetapa = selEtapa
      const { data } = await api.get<{ data: RelatorioResult }>('/relatorios/resultados-etapa', { params })
      setPdfResultadosEtapa(data.data)
    } catch (err: any) {
      setAlert({ type: 'error', message: err?.response?.data?.error || 'Erro ao gerar relatorio de resultados da etapa' })
    } finally { setGerandoEtapa(false) }
  }

  async function handleGerarMedalhasEtapa() {
    if (selEtapa === '' || selCampeonato === '') return
    setAlert(null); setPdfMedalhasEtapa(null); setGerandoEtapa(true)
    try {
      const params: any = { idcampeonato: selCampeonato, top: selTop }
      if (selEtapa !== 'todas') params.idetapa = selEtapa
      const { data } = await api.get<{ data: RelatorioResult }>('/relatorios/medalhas-etapa', { params })
      setPdfMedalhasEtapa(data.data)
    } catch (err: any) {
      setAlert({ type: 'error', message: err?.response?.data?.error || 'Erro ao gerar relatorio de medalhas da etapa' })
    } finally { setGerandoEtapa(false) }
  }

  async function handleGerarIndividual() {
    if (selInscricao === '') return
    setAlert(null)
    setPdfIndividual(null)
    try {
      const res = await gerarIndividual.mutateAsync(selInscricao as number)
      setPdfIndividual(res.data)
    } catch {
      setAlert({ type: 'error', message: 'Erro ao gerar relatório de resultado individual' })
    }
  }

  async function handleGerarRanking() {
    if (selCampeonato === '' || selModalidade === '') return
    setAlert(null)
    setPdfRanking(null)
    try {
      const res = await gerarRanking.mutateAsync({
        idcampeonato: selCampeonato as number,
        idmodalidade: selModalidade !== 'all' ? selModalidade as number : undefined,
      })
      setPdfRanking(res.data)
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Erro ao gerar relatório de ranking geral'
      setAlert({ type: 'error', message })
      console.error('Erro ao gerar ranking:', err)
    }
  }

  async function handleGerarMedalhas() {
    if (selCampeonato === '') return
    setAlert(null)
    setPdfMedalhas(null)
    try {
      const res = await gerarMedalhas.mutateAsync({
        idcampeonato: selCampeonato as number,
        top: selTop,
        idmodalidade: selModalidade !== '' && selModalidade !== 'all' ? selModalidade as number : undefined,
        idetapa: selEtapaMedalhas !== '' ? selEtapaMedalhas as number : undefined,
      })
      setPdfMedalhas(res.data)
    } catch {
      setAlert({ type: 'error', message: 'Erro ao gerar relatório de medalhas' })
    }
  }

  async function handleGerarParticipantes() {
    if (selCampeonato === '' || selModalidade === '') return
    setAlert(null)
    setPdfParticipantes(null)
    try {
      const res = await gerarParticipantes.mutateAsync({
        idcampeonato: selCampeonato as number,
        idmodalidade: selModalidade !== 'all' ? selModalidade as number : undefined,
      })
      setPdfParticipantes(res.data)
    } catch {
      setAlert({ type: 'error', message: 'Erro ao gerar relatório de participantes' })
    }
  }

  const tabs: { key: TabType; label: string; icon: string }[] = [
    { key: 'individual', label: 'Resultado Individual', icon: '&#127989;' },
    { key: 'ranking', label: 'Ranking Geral', icon: '&#127942;' },
    { key: 'medalhas', label: 'Entrega de Medalhas', icon: '&#127941;' },
    { key: 'participantes', label: 'Participantes', icon: '&#128101;' },
    { key: 'resultados-etapa', label: 'Resultados da Etapa', icon: '&#128202;' },
    { key: 'medalhas-etapa', label: 'Medalhas da Etapa', icon: '&#129351;' },
    { key: 'ranking-maior', label: 'Ranking Maior Pontuacao', icon: '&#11088;' },
    ...(isAdmin ? [{ key: 'inscricoes-atirador' as TabType, label: 'Inscricoes do Atirador', icon: '&#128203;' }] : []),
    ...(isAdmin ? [{ key: 'inscricoes-geral' as TabType, label: 'Todas as Inscricoes', icon: '&#128193;' }] : []),
    { key: 'habitualidade', label: 'Habitualidade / DPA-BASE', icon: '&#128737;' },
  ]

  const isLoadingAny =
    gerarIndividual.isPending || gerarRanking.isPending || gerarMedalhas.isPending || gerarParticipantes.isPending || gerandoEtapa || gerandoRankMaior || gerandoInscAt || gerandoInscGeral

  return (
    <div className="space-y-4">
      <PageHeader
        title="Relatórios"
        description="Geração de PDFs para impressão e compartilhamento"
      />

      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      {/* Tabs */}
      <div className="flex gap-1 bg-compumaq-pretoSuave rounded-lg p-1 flex-wrap">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => {
              setActiveTab(tab.key)
              resetPdfs()
              setAlert(null)
            }}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === tab.key
                ? 'bg-compumaq-pretoSuave shadow text-compumaq-texto'
                : 'text-compumaq-textoSuave hover:text-compumaq-texto'
            }`}
          >
            <span dangerouslySetInnerHTML={{ __html: tab.icon }} className="mr-1" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Relatório Individual ──────────────────────────────────────────── */}
      {activeTab === 'individual' && (
        <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-6 shadow-sm space-y-5">
          <h2 className="text-base font-semibold text-compumaq-texto">Resultado Individual do Atirador</h2>
          <p className="text-sm text-compumaq-textoSuave">
            Gera um PDF com cabeçalho, dados do atirador, arma, pontuação detalhada e colocação na etapa.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Campeonato</label>
              <select
                className={selectClass}
                value={selCampeonato}
                onChange={e => {
                  setSelCampeonato(e.target.value ? parseInt(e.target.value) : '')
                  setSelModalidade('')
                  setSelInscricao('')
                  setPdfIndividual(null)
                }}
              >
                <option value="">Selecione...</option>
                {campeonatosData?.data?.map(c => (
                  <option key={c.idcampeonato} value={c.idcampeonato}>
                    {c.descricao} — {c.temporada}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Modalidade</label>
              <select
                className={selectClass}
                value={selModalidade}
                onChange={e => {
                  setSelModalidade(e.target.value ? parseInt(e.target.value) : '')
                  setSelInscricao('')
                  setPdfIndividual(null)
                }}
                disabled={!selCampeonato}
              >
                <option value="">{selCampeonato ? 'Selecione...' : '— selecione o campeonato —'}</option>
                {modalidades.map(m => (
                  <option key={m.idmodalidade} value={m.idmodalidade}>
                    {m.nome}{m.divisao ? ` — ${m.divisao}` : ''} ({m.grupo})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Inscrição / Atirador</label>
              <select
                className={selectClass}
                value={selInscricao}
                onChange={e => {
                  setSelInscricao(e.target.value ? parseInt(e.target.value) : '')
                  setPdfIndividual(null)
                }}
                disabled={!selModalidade}
              >
                <option value="">{selModalidade ? 'Selecione...' : '— selecione a modalidade —'}</option>
                {inscricoes.map(i => (
                  <option key={i.idinscricao} value={i.idinscricao}>
                    #{i.numero_inscricao} — {i.atirador_nome ?? `Atirador ${i.idatirador}`}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <button
            onClick={handleGerarIndividual}
            disabled={selInscricao === '' || gerarIndividual.isPending}
            className="px-5 py-2.5 bg-compumaq-dourado text-compumaq-texto rounded-lg text-sm font-medium hover:bg-compumaq-doradoClaro disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {gerarIndividual.isPending ? <Spinner size="sm" /> : <span>&#128196;</span>}
            {gerarIndividual.isPending ? 'Gerando...' : 'Gerar PDF'}
          </button>

          {pdfIndividual && (
            <PdfPreviewModal result={pdfIndividual} titulo="Resultado Individual" onClose={() => setPdfIndividual(null)} />
          )}
        </div>
      )}

      {/* ── Ranking Geral ─────────────────────────────────────────────────── */}
      {activeTab === 'ranking' && (
        <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-6 shadow-sm space-y-5">
          <h2 className="text-base font-semibold text-compumaq-texto">Ranking Geral por Modalidade</h2>
          <p className="text-sm text-compumaq-textoSuave">
            Classificação completa com critérios de desempate e marcação de etapas descartadas.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Campeonato</label>
              <select
                className={selectClass}
                value={selCampeonato}
                onChange={e => {
                  setSelCampeonato(e.target.value ? parseInt(e.target.value) : '')
                  setSelModalidade('')
                  setPdfRanking(null)
                }}
              >
                <option value="">Selecione...</option>
                {campeonatosData?.data?.map(c => (
                  <option key={c.idcampeonato} value={c.idcampeonato}>
                    {c.descricao} — {c.temporada}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Modalidade</label>
              <select
                className={selectClass}
                value={selModalidade}
                onChange={e => {
                  const v = e.target.value
                  setSelModalidade(v === 'all' ? 'all' : v ? parseInt(v) : '')
                  setPdfRanking(null)
                }}
                disabled={!selCampeonato}
              >
                <option value="">{selCampeonato ? 'Selecione...' : '— selecione o campeonato —'}</option>
                {selCampeonato && (
                  <option value="all">Todas as Modalidades</option>
                )}
                {modalidades.map(m => (
                  <option key={m.idmodalidade} value={m.idmodalidade}>
                    {m.nome}{m.divisao ? ` — ${m.divisao}` : ''} ({m.grupo})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <button
            onClick={handleGerarRanking}
            disabled={selCampeonato === '' || selModalidade === '' || gerarRanking.isPending}
            className="px-5 py-2.5 bg-compumaq-dourado text-compumaq-texto rounded-lg text-sm font-medium hover:bg-compumaq-doradoClaro disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {gerarRanking.isPending ? <Spinner size="sm" /> : <span>&#127942;</span>}
            {gerarRanking.isPending ? 'Gerando...' : selModalidade === 'all' ? 'Gerar PDF Todas as Modalidades' : 'Gerar PDF Ranking Geral'}
          </button>

          {pdfRanking && (
            <PdfPreviewModal result={pdfRanking} titulo="Ranking Geral" onClose={() => setPdfRanking(null)} />
          )}
        </div>
      )}

      {/* ── Medalhas Top N ────────────────────────────────────────────────── */}
      {activeTab === 'medalhas' && (
        <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-6 shadow-sm space-y-5">
          <h2 className="text-base font-semibold text-compumaq-texto">Entrega de Medalhas — Top N</h2>
          <p className="text-sm text-compumaq-textoSuave">
            Relatório com os N melhores de cada modalidade em um único PDF. Deixe a modalidade em branco para incluir todas.
            Deixe a etapa em "Todas as etapas" para somar o campeonato inteiro (com descarte, se configurado), ou escolha uma etapa para ver só o resultado dela.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Campeonato</label>
              <select
                className={selectClass}
                value={selCampeonato}
                onChange={e => {
                  setSelCampeonato(e.target.value ? parseInt(e.target.value) : '')
                  setSelModalidade('')
                  setSelEtapaMedalhas('')
                  setPdfMedalhas(null)
                }}
              >
                <option value="">Selecione...</option>
                {campeonatosData?.data?.map(c => (
                  <option key={c.idcampeonato} value={c.idcampeonato}>
                    {c.descricao} — {c.temporada}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Modalidade (opcional)</label>
              <select
                className={selectClass}
                value={selModalidade}
                onChange={e => {
                  setSelModalidade(e.target.value ? parseInt(e.target.value) : '')
                  setPdfMedalhas(null)
                }}
                disabled={!selCampeonato}
              >
                <option value="">Todas as modalidades</option>
                {modalidades.map(m => (
                  <option key={m.idmodalidade} value={m.idmodalidade}>
                    {m.nome}{m.divisao ? ` — ${m.divisao}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Etapa</label>
              <select
                className={selectClass}
                value={selEtapaMedalhas}
                onChange={e => { setSelEtapaMedalhas(e.target.value ? parseInt(e.target.value) : ''); setPdfMedalhas(null) }}
                disabled={!selCampeonato}
              >
                <option value="">Todas as etapas</option>
                {etapasEtapaUnicas.map(e => (
                  <option key={e.numero_etapa} value={e.idetapa}>{e.titulo_reduzido || e.titulo || `Etapa ${e.numero_etapa}`}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Quantidade (Top N)</label>
              <select
                className={selectClass}
                value={selTop}
                onChange={e => { setSelTop(parseInt(e.target.value)); setPdfMedalhas(null) }}
              >
                {[1, 2, 3, 5, 8, 10].map(n => (
                  <option key={n} value={n}>Top {n}</option>
                ))}
              </select>
            </div>
          </div>

          <button
            onClick={handleGerarMedalhas}
            disabled={selCampeonato === '' || gerarMedalhas.isPending}
            className="px-5 py-2.5 bg-compumaq-dourado text-compumaq-texto rounded-lg text-sm font-medium hover:bg-compumaq-doradoClaro disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {gerarMedalhas.isPending ? <Spinner size="sm" /> : <span>&#127941;</span>}
            {gerarMedalhas.isPending ? 'Gerando...' : `Gerar PDF Top ${selTop}`}
          </button>

          {pdfMedalhas && (
            <PdfPreviewModal result={pdfMedalhas} titulo={`Medalhas Top ${selTop}`} onClose={() => setPdfMedalhas(null)} />
          )}
        </div>
      )}

      {/* ── Participantes ─────────────────────────────────────────────────── */}
      {activeTab === 'participantes' && (
        <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-6 shadow-sm space-y-5">
          <h2 className="text-base font-semibold text-compumaq-texto">Relatório Geral de Participantes</h2>
          <p className="text-sm text-compumaq-textoSuave">
            Todos os atiradores com resultado lançado: colocação no ranking, clube e pontuação total.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Campeonato</label>
              <select
                className={selectClass}
                value={selCampeonato}
                onChange={e => {
                  setSelCampeonato(e.target.value ? parseInt(e.target.value) : '')
                  setSelModalidade('')
                  setPdfParticipantes(null)
                }}
              >
                <option value="">Selecione...</option>
                {campeonatosData?.data?.map(c => (
                  <option key={c.idcampeonato} value={c.idcampeonato}>
                    {c.descricao} — {c.temporada}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Modalidade</label>
              <select
                className={selectClass}
                value={selModalidade}
                onChange={e => {
                  const v = e.target.value
                  setSelModalidade(v === 'all' ? 'all' : v ? parseInt(v) : '')
                  setPdfParticipantes(null)
                }}
                disabled={!selCampeonato}
              >
                <option value="">{selCampeonato ? 'Selecione...' : '— selecione o campeonato —'}</option>
                {selCampeonato && (
                  <option value="all">Todas as Modalidades</option>
                )}
                {modalidades.map(m => (
                  <option key={m.idmodalidade} value={m.idmodalidade}>
                    {m.nome}{m.divisao ? ` — ${m.divisao}` : ''} ({m.grupo})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <button
            onClick={handleGerarParticipantes}
            disabled={selCampeonato === '' || selModalidade === '' || gerarParticipantes.isPending}
            className="px-5 py-2.5 bg-compumaq-dourado text-compumaq-texto rounded-lg text-sm font-medium hover:bg-compumaq-doradoClaro disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {gerarParticipantes.isPending ? <Spinner size="sm" /> : <span>&#128101;</span>}
            {gerarParticipantes.isPending ? 'Gerando...' : selModalidade === 'all' ? 'Gerar PDF Todas as Modalidades' : 'Gerar PDF Participantes'}
          </button>

          {pdfParticipantes && (
            <PdfPreviewModal result={pdfParticipantes} titulo="Participantes" onClose={() => setPdfParticipantes(null)} />
          )}
        </div>
      )}

      {/* ── Resultados da Etapa ───────────────────────────────────────────── */}
      {activeTab === 'resultados-etapa' && (
        <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-6 shadow-sm space-y-5">
          <h2 className="text-base font-semibold text-compumaq-texto">Resultados da Etapa</h2>
          <p className="text-sm text-compumaq-textoSuave">
            Lista completa dos atletas classificados em cada modalidade de uma etapa especifica, agrupados por modalidade/categoria (Overall e Dama).
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Campeonato</label>
              <select
                className={selectClass}
                value={selCampeonato}
                onChange={e => {
                  setSelCampeonato(e.target.value ? parseInt(e.target.value) : '')
                  setSelEtapa('')
                  setPdfResultadosEtapa(null)
                }}
              >
                <option value="">Selecione...</option>
                {campeonatosData?.data?.map(c => (
                  <option key={c.idcampeonato} value={c.idcampeonato}>{c.descricao} — {c.temporada}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Etapa</label>
              <select
                className={selectClass}
                value={selEtapa}
                onChange={e => { setSelEtapa(e.target.value === '' ? '' : e.target.value === 'todas' ? 'todas' : parseInt(e.target.value)); setPdfResultadosEtapa(null) }}
                disabled={!selCampeonato}
              >
                <option value="">{selCampeonato ? 'Selecione...' : '— selecione o campeonato —'}</option>
                {selCampeonato && <option value="todas">Todas as etapas</option>}
                {etapasEtapaUnicas.map(e => (
                  <option key={e.numero_etapa} value={e.idetapa}>{e.titulo_reduzido || e.titulo || `Etapa ${e.numero_etapa}`}</option>
                ))}
              </select>
            </div>
          </div>

          <button
            onClick={handleGerarResultadosEtapa}
            disabled={selEtapa === '' || gerandoEtapa}
            className="px-5 py-2.5 bg-compumaq-dourado text-compumaq-texto rounded-lg text-sm font-medium hover:bg-compumaq-doradoClaro disabled:opacity-50 flex items-center gap-2"
          >
            {gerandoEtapa ? <Spinner size="sm" /> : <span>📊</span>}
            {gerandoEtapa ? 'Gerando...' : 'Gerar PDF Resultados da Etapa'}
          </button>

          {pdfResultadosEtapa && (
            <PdfPreviewModal result={pdfResultadosEtapa} titulo="Resultados da Etapa" onClose={() => setPdfResultadosEtapa(null)} />
          )}
        </div>
      )}

      {/* ── Habitualidade / DPA-BASE ─────────────────────────────────────── */}
      {activeTab === 'habitualidade' && (
        <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-6 shadow-sm space-y-5">
          <h2 className="text-base font-semibold text-compumaq-texto">Habitualidade / DPA-BASE</h2>
          <p className="text-sm text-compumaq-textoSuave">
            Participacoes dos atiradores na etapa (todas as modalidades), com CPF, CR, arma, municao e classificacao,
            para o lancamento no DPA-BASE da Policia Federal (Portaria DG/PF 19.109/2026): a entidade promotora tem
            30 dias apos o termino da etapa. O PDF traz o prazo e as pendencias (atiradores sem CR, inscritos sem resultado).
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Campeonato</label>
              <select
                className={selectClass}
                aria-label="Campeonato habitualidade"
                value={selCampeonato}
                onChange={e => { setSelCampeonato(e.target.value ? parseInt(e.target.value) : ''); setSelEtapa('') }}
              >
                <option value="">Selecione...</option>
                {campeonatosData?.data?.map(c => (
                  <option key={c.idcampeonato} value={c.idcampeonato}>{c.descricao} — {c.temporada}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Etapa</label>
              <select
                className={selectClass}
                aria-label="Etapa habitualidade"
                value={selEtapa === 'todas' ? '' : selEtapa}
                onChange={e => setSelEtapa(e.target.value === '' ? '' : parseInt(e.target.value))}
                disabled={!selCampeonato}
              >
                <option value="">{selCampeonato ? 'Selecione...' : '— selecione o campeonato —'}</option>
                {etapasEtapaUnicas.map(e => (
                  <option key={e.numero_etapa} value={e.idetapa}>{e.titulo_reduzido || e.titulo || `Etapa ${e.numero_etapa}`}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => handleHabitualidade('csv')}
              disabled={selEtapa === '' || selEtapa === 'todas' || gerandoHabit !== null}
              className="px-5 py-2.5 bg-compumaq-dourado text-compumaq-texto rounded-lg text-sm font-medium hover:bg-compumaq-doradoClaro disabled:opacity-50 flex items-center gap-2"
            >
              {gerandoHabit === 'csv' ? <Spinner size="sm" /> : <span>📄</span>}
              {gerandoHabit === 'csv' ? 'Gerando...' : 'Baixar planilha (CSV)'}
            </button>
            <button
              onClick={() => handleHabitualidade('pdf')}
              disabled={selEtapa === '' || selEtapa === 'todas' || gerandoHabit !== null}
              className="px-5 py-2.5 border border-compumaq-dourado/50 text-compumaq-texto rounded-lg text-sm font-medium hover:bg-compumaq-preto disabled:opacity-50 flex items-center gap-2"
            >
              {gerandoHabit === 'pdf' ? <Spinner size="sm" /> : <span>🖨️</span>}
              {gerandoHabit === 'pdf' ? 'Gerando...' : 'Baixar PDF'}
            </button>
          </div>
        </div>
      )}

      {/* ── Medalhas da Etapa ─────────────────────────────────────────────── */}
      {activeTab === 'medalhas-etapa' && (
        <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-6 shadow-sm space-y-5">
          <h2 className="text-base font-semibold text-compumaq-texto">Medalhas da Etapa</h2>
          <p className="text-sm text-compumaq-textoSuave">
            Top N de cada modalidade da etapa selecionada (Overall e Dama), formato compacto para premiacao.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Campeonato</label>
              <select
                className={selectClass}
                value={selCampeonato}
                onChange={e => {
                  setSelCampeonato(e.target.value ? parseInt(e.target.value) : '')
                  setSelEtapa('')
                  setPdfMedalhasEtapa(null)
                }}
              >
                <option value="">Selecione...</option>
                {campeonatosData?.data?.map(c => (
                  <option key={c.idcampeonato} value={c.idcampeonato}>{c.descricao} — {c.temporada}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Etapa</label>
              <select
                className={selectClass}
                value={selEtapa}
                onChange={e => { setSelEtapa(e.target.value === '' ? '' : e.target.value === 'todas' ? 'todas' : parseInt(e.target.value)); setPdfMedalhasEtapa(null) }}
                disabled={!selCampeonato}
              >
                <option value="">{selCampeonato ? 'Selecione...' : '— selecione o campeonato —'}</option>
                {selCampeonato && <option value="todas">Todas as etapas</option>}
                {etapasEtapaUnicas.map(e => (
                  <option key={e.numero_etapa} value={e.idetapa}>{e.titulo_reduzido || e.titulo || `Etapa ${e.numero_etapa}`}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Top N</label>
              <select className={selectClass} value={selTop} onChange={e => { setSelTop(parseInt(e.target.value)); setPdfMedalhasEtapa(null) }}>
                {[1, 2, 3, 5, 8, 10].map(n => <option key={n} value={n}>Top {n}</option>)}
              </select>
            </div>
          </div>

          <button
            onClick={handleGerarMedalhasEtapa}
            disabled={selEtapa === '' || gerandoEtapa}
            className="px-5 py-2.5 bg-compumaq-dourado text-compumaq-texto rounded-lg text-sm font-medium hover:bg-compumaq-doradoClaro disabled:opacity-50 flex items-center gap-2"
          >
            {gerandoEtapa ? <Spinner size="sm" /> : <span>🏅</span>}
            {gerandoEtapa ? 'Gerando...' : `Gerar PDF Medalhas Top ${selTop}`}
          </button>

          {pdfMedalhasEtapa && (
            <PdfPreviewModal result={pdfMedalhasEtapa} titulo={`Medalhas da Etapa - Top ${selTop}`} onClose={() => setPdfMedalhasEtapa(null)} />
          )}
        </div>
      )}

      {/* ── Ranking por Maior Pontuacao ───────────────────────────────────── */}
      {activeTab === 'ranking-maior' && (
        <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-6 shadow-sm space-y-5">
          <h2 className="text-base font-semibold text-compumaq-texto">Ranking por Maior Pontuacao</h2>
          <p className="text-sm text-compumaq-textoSuave">
            Lista, por modalidade, todos os atletas ordenados pela <b>maior pontuacao</b> que cada um obteve no campeonato (em qualquer etapa).
            Cada atirador aparece UMA vez com seu melhor resultado e a quantidade de provas que participou.
            Tiebreakers: para Precisao usa X, 10, 9, 8 + idade; para Silhueta usa carneiro, peru, porco, galinha + idade.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Campeonato</label>
              <select
                className={selectClass}
                value={selCampeonato}
                onChange={e => {
                  setSelCampeonato(e.target.value ? parseInt(e.target.value) : '')
                  setSelModalidade('')
                  setPdfRankingMaior(null)
                }}
              >
                <option value="">Selecione...</option>
                {campeonatosData?.data?.map(c => (
                  <option key={c.idcampeonato} value={c.idcampeonato}>{c.descricao} — {c.temporada}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Modalidade (opcional)</label>
              <select
                className={selectClass}
                value={selModalidade}
                onChange={e => { setSelModalidade(e.target.value ? parseInt(e.target.value) : ''); setPdfRankingMaior(null) }}
                disabled={!selCampeonato}
              >
                <option value="">Todas as modalidades</option>
                {modalidades.map(m => (
                  <option key={m.idmodalidade} value={m.idmodalidade}>
                    {m.nome}{m.divisao ? ` — ${m.divisao}` : ''} ({m.grupo})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <button
            onClick={handleGerarRankingMaior}
            disabled={selCampeonato === '' || gerandoRankMaior}
            className="px-5 py-2.5 bg-compumaq-dourado text-compumaq-texto rounded-lg text-sm font-medium hover:bg-compumaq-doradoClaro disabled:opacity-50 flex items-center gap-2"
          >
            {gerandoRankMaior ? <Spinner size="sm" /> : <span>⭐</span>}
            {gerandoRankMaior ? 'Gerando...' : 'Gerar PDF Ranking Maior Pontuacao'}
          </button>

          {pdfRankingMaior && (
            <PdfPreviewModal result={pdfRankingMaior} titulo="Ranking por Maior Pontuacao" onClose={() => setPdfRankingMaior(null)} />
          )}
        </div>
      )}

      {/* ── Inscricoes do Atirador (somente admin) ─────────────────────────── */}
      {activeTab === 'inscricoes-atirador' && isAdmin && (
        <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-6 shadow-sm space-y-5">
          <h2 className="text-base font-semibold text-compumaq-texto">Inscricoes do Atirador</h2>
          <p className="text-sm text-compumaq-textoSuave">
            Lista todas as inscricoes do atirador selecionado, separadas em <b>Concluidas</b> (com resultado lancado) e <b>Pendentes</b> (sem resultado).
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Buscar atirador (3+ letras)</label>
              <input
                type="text"
                className="w-full border border-compumaq-dourado/25 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-compumaq-dourado focus:border-compumaq-dourado"
                placeholder="Digite o nome..."
                value={searchAtiradorRel}
                onChange={e => setSearchAtiradorRel(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Atirador</label>
              <select
                className={selectClass}
                value={selAtiradorRel}
                onChange={e => { setSelAtiradorRel(e.target.value ? parseInt(e.target.value) : ''); setPdfInscricoesAt(null) }}
              >
                <option value="">Selecione...</option>
                {atiradoresRel.map(a => (
                  <option key={a.idatirador} value={a.idatirador}>{a.nome} — {a.cpf}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Etapa</label>
              <select
                className={selectClass}
                value={selEtapaRel}
                onChange={e => { setSelEtapaRel(e.target.value ? parseInt(e.target.value) : ''); setPdfInscricoesAt(null) }}
              >
                <option value="">Todas as etapas</option>
                {[1, 2, 3, 4, 5, 6].map(n => (
                  <option key={n} value={n}>{n}a Etapa</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Modalidade</label>
              <select
                className={selectClass}
                value={selModalidadeRel}
                onChange={e => { setSelModalidadeRel(e.target.value ? parseInt(e.target.value) : ''); setPdfInscricoesAt(null) }}
              >
                <option value="">Todas as modalidades</option>
                {modalidadesRel.map(m => (
                  <option key={m.idmodalidade} value={m.idmodalidade}>{m.nome}{m.divisao ? ` — ${m.divisao}` : ''}</option>
                ))}
              </select>
            </div>
          </div>
          {searchAtiradorRel.length >= 3 && atiradoresRel.length === 0 && (
            <p className="text-xs text-compumaq-textoSuave">Nenhum atirador encontrado.</p>
          )}

          <button
            onClick={handleGerarInscricoesAtirador}
            disabled={selAtiradorRel === '' || gerandoInscAt}
            className="px-5 py-2.5 bg-compumaq-dourado text-compumaq-texto rounded-lg text-sm font-medium hover:bg-compumaq-doradoClaro disabled:opacity-50 flex items-center gap-2"
          >
            {gerandoInscAt ? <Spinner size="sm" /> : <span>📋</span>}
            {gerandoInscAt ? 'Gerando...' : 'Gerar PDF Inscricoes do Atirador'}
          </button>

          {pdfInscricoesAt && (
            <PdfPreviewModal result={pdfInscricoesAt} titulo="Inscricoes do Atirador" onClose={() => setPdfInscricoesAt(null)} />
          )}
        </div>
      )}

      {/* ── Todas as Inscricoes (somente admin) ─────────────────────────────── */}
      {activeTab === 'inscricoes-geral' && isAdmin && (
        <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-6 shadow-sm space-y-5">
          <h2 className="text-base font-semibold text-compumaq-texto">Todas as Inscricoes</h2>
          <p className="text-sm text-compumaq-textoSuave">
            Busca todas as inscricoes feitas no sistema. Todos os filtros sao opcionais — deixe em "Todos/Todas" para nao restringir.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Campeonato</label>
              <select
                className={selectClass}
                value={selCampeonatoInscGeral}
                onChange={e => { setSelCampeonatoInscGeral(e.target.value ? parseInt(e.target.value) : ''); setPdfInscricoesGeral(null) }}
              >
                <option value="">Todos os campeonatos</option>
                {campeonatosData?.data?.map(c => (
                  <option key={c.idcampeonato} value={c.idcampeonato}>{c.descricao} — {c.temporada}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Modalidade</label>
              <select
                className={selectClass}
                value={selModalidadeInscGeral}
                onChange={e => { setSelModalidadeInscGeral(e.target.value ? parseInt(e.target.value) : ''); setPdfInscricoesGeral(null) }}
              >
                <option value="">Todas as modalidades</option>
                {modalidadesRel.map(m => (
                  <option key={m.idmodalidade} value={m.idmodalidade}>{m.nome}{m.divisao ? ` — ${m.divisao}` : ''}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Etapa</label>
              <select
                className={selectClass}
                value={selEtapaInscGeral}
                onChange={e => { setSelEtapaInscGeral(e.target.value ? parseInt(e.target.value) : ''); setPdfInscricoesGeral(null) }}
              >
                <option value="">Todas as etapas</option>
                {[1, 2, 3, 4, 5, 6].map(n => (
                  <option key={n} value={n}>{n}a Etapa</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Atirador</label>
              {selAtiradorInscGeral !== '' ? (
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800 truncate">
                    {atiradoresInscGeral.find(a => a.idatirador === selAtiradorInscGeral)?.nome ?? `Atirador #${selAtiradorInscGeral}`}
                  </div>
                  <button
                    type="button"
                    onClick={() => { setSelAtiradorInscGeral(''); setSearchAtiradorInscGeral(''); setPdfInscricoesGeral(null) }}
                    className="px-3 py-2 text-sm text-red-600 hover:text-red-800 border border-compumaq-dourado/25 rounded-lg hover:bg-compumaq-preto"
                  >
                    Limpar
                  </button>
                </div>
              ) : (
                <input
                  type="text"
                  className="w-full border border-compumaq-dourado/25 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-compumaq-dourado focus:border-compumaq-dourado"
                  placeholder="Todos — digite 3+ letras p/ buscar..."
                  value={searchAtiradorInscGeral}
                  onChange={e => setSearchAtiradorInscGeral(e.target.value)}
                />
              )}
            </div>
          </div>

          {selAtiradorInscGeral === '' && searchAtiradorInscGeral.length >= 3 && (
            atiradoresInscGeral.length === 0 ? (
              <p className="text-xs text-compumaq-textoSuave">Nenhum atirador encontrado.</p>
            ) : (
              <div className="border border-compumaq-dourado/25 rounded-lg divide-y divide-compumaq-dourado/25 max-h-40 overflow-y-auto">
                {atiradoresInscGeral.map(a => (
                  <button
                    key={a.idatirador}
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-compumaq-preto"
                    onClick={() => { setSelAtiradorInscGeral(a.idatirador); setPdfInscricoesGeral(null) }}
                  >
                    <span className="font-medium">{a.nome}</span>
                    <span className="text-compumaq-textoSuave text-xs ml-2">{a.cpf}</span>
                  </button>
                ))}
              </div>
            )
          )}

          <button
            onClick={handleGerarInscricoesGeral}
            disabled={gerandoInscGeral}
            className="px-5 py-2.5 bg-compumaq-dourado text-compumaq-texto rounded-lg text-sm font-medium hover:bg-compumaq-doradoClaro disabled:opacity-50 flex items-center gap-2"
          >
            {gerandoInscGeral ? <Spinner size="sm" /> : <span>🗂️</span>}
            {gerandoInscGeral ? 'Gerando...' : 'Gerar PDF de Inscricoes'}
          </button>

          {pdfInscricoesGeral && (
            <PdfPreviewModal result={pdfInscricoesGeral} titulo="Todas as Inscricoes" onClose={() => setPdfInscricoesGeral(null)} />
          )}
        </div>
      )}

      {isLoadingAny && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50 pointer-events-none">
          <div className="bg-compumaq-pretoSuave rounded-xl px-8 py-6 shadow-xl flex items-center gap-4">
            <Spinner size="lg" />
            <span className="text-compumaq-texto font-medium">Gerando PDF...</span>
          </div>
        </div>
      )}
    </div>
  )
}
